// Thermal receipt / kitchen ticket printing.
//
// Renders the order as a plain, black-and-white 80mm receipt (monospace, no
// colors, like a real restaurant ticket) in a hidden popup window and triggers
// the browser print dialog. The restaurant's thermal/receipt printer appears as
// a normal system printer, so no driver or backend integration is required —
// the cashier just picks it (or sets it as default) in the print dialog.

function esc(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function money(value) {
  return Number(value || 0).toFixed(3);
}

function fmtDateTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("fr-FR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function customerName(order) {
  const user = order.user || order.users;
  if (!user) return "";
  if (user.first_name) return `${user.first_name} ${user.last_name || ""}`.trim();
  return user.name || "";
}

function customerPhone(order) {
  const user = order.user || order.users;
  return (user && user.phone) || "";
}

function addressLine(order) {
  const a = order.address;
  if (!a) return "";
  return a.address || a.street || a.label || "";
}

function buildProducts(order) {
  return (order.products || []).map((p) => {
    const qty = Number(p.quantity || (p.pivot && p.pivot.quantity) || 1);
    const name = p.name || (p.product && p.product.name) || "Produit";
    const options = Array.isArray(p.items)
      ? p.items.map((i) => (i && i.name ? i.name : i)).filter((x) => x && typeof x === "string")
      : [];
    const note = (p.pivot && p.pivot.specifications) || p.specifications || "";
    const lineTotal = p.total_price || (p.pivot && p.pivot.total) || (Number(p.price || 0) * qty) || 0;
    return { qty, name, options, note, lineTotal };
  });
}

function buildTicketHtml(order, restaurant) {
  const restaurantName = (restaurant && restaurant.name) || "Restaurant";
  const ref = order.ref || order.reference || "Référence indisponible";
  const isDelivery = order.orderType !== "takeaway";
  const products = buildProducts(order);

  const subtotal = products.reduce((s, p) => s + Number(p.lineTotal || 0), 0);
  const delivery = Number(order.deliveryCost || 0);
  const total = order.total_price != null
    ? order.total_price
    : (order.total != null ? order.total : (order.orderCost != null ? order.orderCost : subtotal + delivery));

  const name = customerName(order);
  const phone = customerPhone(order);
  const address = isDelivery ? addressLine(order) : "";
  const prep = Number(order.estimated_preparation_time || 0);

  const itemsHtml = products.map((p) => `
    <div class="item">
      <div class="row">
        <span class="bold">${p.qty} x ${esc(p.name)}</span>
        <span>${money(p.lineTotal)}</span>
      </div>
      ${p.options.map((o) => `<div class="sub">+ ${esc(o)}</div>`).join("")}
      ${p.note ? `<div class="note">** ${esc(p.note)}</div>` : ""}
    </div>`).join("");

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Ticket ${esc(ref)}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Courier New', Courier, monospace; color: #000; background: #fff; width: 80mm; padding: 4mm 3mm; font-size: 12px; line-height: 1.4; }
  .center { text-align: center; }
  .bold { font-weight: bold; }
  .big { font-size: 16px; }
  .hr { border-top: 1px dashed #000; margin: 6px 0; }
  .row { display: flex; justify-content: space-between; gap: 8px; }
  .item { margin: 5px 0; }
  .sub { padding-left: 14px; font-size: 11px; }
  .note { padding-left: 14px; font-size: 11px; font-style: italic; }
  .tot { font-size: 15px; font-weight: bold; }
  .muted { font-size: 11px; }
  @media print { @page { size: 80mm auto; margin: 0; } body { width: 80mm; } }
</style>
</head>
<body>
  <div class="center bold big">${esc(restaurantName)}</div>
  <div class="center muted">TICKET CUISINE</div>
  <div class="hr"></div>

  <div class="row bold">
    <span>#${esc(ref)}</span>
    <span>${isDelivery ? "LIVRAISON" : "A EMPORTER"}</span>
  </div>
  <div class="muted">${esc(fmtDateTime(order.created_at))}</div>

  ${name || phone || address ? `<div class="hr"></div>` : ""}
  ${name ? `<div>Client: ${esc(name)}</div>` : ""}
  ${phone ? `<div>Tel: ${esc(phone)}</div>` : ""}
  ${address ? `<div>Adresse: ${esc(address)}</div>` : ""}

  <div class="hr"></div>
  ${itemsHtml || '<div class="center muted">Aucun produit</div>'}
  <div class="hr"></div>

  <div class="row"><span>Sous-total</span><span>${money(subtotal)}</span></div>
  ${delivery > 0 ? `<div class="row"><span>Livraison</span><span>${money(delivery)}</span></div>` : ""}
  <div class="row tot"><span>TOTAL</span><span>${money(total)} TND</span></div>

  ${prep > 0 ? `<div class="hr"></div><div class="center">Preparation: ${prep} min</div>` : ""}
  <div class="hr"></div>
  <div class="center muted">Merci !</div>

  <script>
    window.onload = function () { window.focus(); window.print(); };
    window.onafterprint = function () { window.close(); };
  </script>
</body>
</html>`;
}

/**
 * Open the print dialog for an order's kitchen/receipt ticket.
 * `order` should be the full order (from getOrderDetail) so options, notes and
 * the address are present. `restaurant` provides the header name.
 */
export function printOrderTicket(order, restaurant) {
  if (!order) return;
  const html = buildTicketHtml(order, restaurant);
  // Opened synchronously from a click handler, so it is not blocked as a popup.
  const win = window.open("", "_blank", "width=380,height=640");
  if (!win) return; // popup blocked — caller may surface a hint
  win.document.open();
  win.document.write(html);
  win.document.close();
}
